import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Tool, Employee, AppSettings, GovernanceTask, Curriculum, ToolLimitResult, DEFAULT_CRITICALITY_LEVELS, CriticalityLevel } from '../types';
import { Edit2, Trash2, Plus, Search, User, Tag, Download, Filter, ArrowUpDown, Check, X, FileDown, HelpCircle, AlertCircle, ThumbsUp, CheckSquare, Calendar, Star, Zap, Eye } from 'lucide-react';
import { EmptyState } from '../components/EmptyState';
import { ToolModal } from '../components/ToolModal';
import { CsvImporter } from '../components/CsvImporter';
import { UpgradeModal } from '../components/UpgradeModal';
import { InfoTooltip } from '../components/InfoTooltip';

const ICON_MAP: Record<string, React.FC<any>> = {
  AlertCircle, ThumbsUp, HelpCircle, Star, Zap, Eye,
};

const COLOR_MAP: Record<string, { bg: string; border: string; text: string }> = {
  red: { bg: 'bg-red-100', border: 'border-red-200', text: 'text-red-600' },
  orange: { bg: 'bg-orange-100', border: 'border-orange-200', text: 'text-orange-600' },
  amber: { bg: 'bg-amber-100', border: 'border-amber-200', text: 'text-amber-600' },
  emerald: { bg: 'bg-emerald-100', border: 'border-emerald-200', text: 'text-emerald-600' },
  blue: { bg: 'bg-blue-100', border: 'border-blue-200', text: 'text-blue-600' },
  purple: { bg: 'bg-purple-100', border: 'border-purple-200', text: 'text-purple-600' },
};

function getLevelForTool(tool: Tool, levels: CriticalityLevel[]): CriticalityLevel | undefined {
  return levels.find(l => l.id === tool.importance);
}

interface InventoryViewProps {
  tools: Tool[];
  employees: Employee[];
  onAdd: (tool: Tool) => Promise<void>;
  onUpdate: (tool: Tool) => Promise<void>;
  onDelete: (id: string) => void;
  onAddGovernanceTask: (task: GovernanceTask) => Promise<void>;
  curricula?: Curriculum[];
  settings: AppSettings;
  onCheckToolLimit?: () => Promise<ToolLimitResult | null>;
  toolLimit?: ToolLimitResult | null;
  onUpgrade?: () => void;
}

type SortField = 'name' | 'monthlyCost' | 'userCount' | 'satisfactionScore';

const CURRENCY_SYMBOLS: Record<string, string> = { EUR: '€', USD: '$', GBP: '£', CHF: 'Fr.', JPY: '¥', CAD: 'CA$', AUD: 'A$', CNY: '¥' };
function getCurrencySymbol(code: string | undefined, settings: AppSettings): string {
  if (!code) return settings.currencySymbol;
  const fromRates = settings.currencyRates?.find(r => r.code === code)?.symbol;
  return fromRates ?? CURRENCY_SYMBOLS[code] ?? code;
}

export const InventoryView: React.FC<InventoryViewProps> = ({ tools, employees, onAdd, onUpdate, onDelete, onAddGovernanceTask, curricula = [], settings, onCheckToolLimit, toolLimit, onUpgrade }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState('');
  
  // Filter State
  const [showFilters, setShowFilters] = useState(false);
  const [filterCategory, setFilterCategory] = useState<string>('All');
  const [filterImportance, setFilterImportance] = useState<string>('All');

  useEffect(() => {
    const importance = searchParams.get('importance');
    if (importance) {
      setFilterImportance(importance);
      setShowFilters(true);
      searchParams.delete('importance');
      setSearchParams(searchParams, { replace: true });
    }
  }, []);

  // Sort State
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [sortConfig, setSortConfig] = useState<{ field: SortField, direction: 'asc' | 'desc' }>({ 
    field: 'name', 
    direction: 'asc' 
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTool, setEditingTool] = useState<Tool | undefined>(undefined);
  const [taskTool, setTaskTool] = useState<Tool | null>(null);
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [newTaskDueDate, setNewTaskDueDate] = useState('');
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // Filter & Sort Logic
  const filteredTools = tools
    .filter(tool => {
      const term = searchTerm.toLowerCase();
      const matchesSearch = tool.name.toLowerCase().includes(term) || 
                            tool.tags?.some(tag => tag.toLowerCase().includes(term));
      const matchesCategory = filterCategory === 'All' || tool.category === filterCategory;
      const matchesImportance = filterImportance === 'All' || tool.importance === filterImportance;
      return matchesSearch && matchesCategory && matchesImportance;
    })
    .sort((a, b) => {
        let valA: any = a[sortConfig.field];
        let valB: any = b[sortConfig.field];

        // Handle string comparison for names
        if (typeof valA === 'string') {
            valA = valA.toLowerCase();
            valB = valB.toLowerCase();
        }

        if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
        if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
    });

  const handleEdit = (tool: Tool) => {
    setEditingTool(tool);
    setIsModalOpen(true);
  };

  const handleAddNew = async () => {
    if (onCheckToolLimit) {
      const result = await onCheckToolLimit();
      if (result && !result.allowed) {
        setShowUpgradeModal(true);
        return;
      }
    }
    setEditingTool(undefined);
    setIsModalOpen(true);
  };

  const handleSave = async (tool: Tool) => {
    if (editingTool) {
      await onUpdate(tool);
    } else {
      await onAdd(tool);
    }
  };

  const handleSort = (field: SortField) => {
      setSortConfig(current => ({
          field,
          direction: current.field === field && current.direction === 'asc' ? 'desc' : 'asc'
      }));
      setShowSortMenu(false);
  };

  const handleCsvImport = async (data: string[][]) => {
    if (data.length < 2) return;

    if (onCheckToolLimit) {
      const result = await onCheckToolLimit();
      const rowCount = data.length - 1;
      if (result && result.current + rowCount > result.limit) {
        setShowUpgradeModal(true);
        return;
      }
    }

    const headers = data[0].map(h => h.toLowerCase());
    const rows = data.slice(1);
    let successCount = 0;

    rows.forEach(row => {
        if (row.length < 2) return; // Skip empty rows

        // Helper to find value by possible header names
        const getValue = (keys: string[]) => {
            const index = headers.findIndex(h => keys.some(k => h.includes(k)));
            return index !== -1 ? row[index] : '';
        };

        const name = getValue(['name', 'tool']);
        if (!name) return;

        const categoryStr = getValue(['category', 'type']);
        // Check if category exists in settings, if not use default or Other
        const category = (settings.categories || []).find(c => c.toLowerCase() === categoryStr.toLowerCase()) || 'Other';

        const importanceStr = getValue(['importance', 'criticality']);
        const critLevels = settings.criticalityLevels ?? DEFAULT_CRITICALITY_LEVELS;
        const importance = critLevels.find(l => l.label.toLowerCase() === importanceStr.toLowerCase() || l.id.toLowerCase() === importanceStr.toLowerCase())?.id
          ?? critLevels.find(l => l.sortOrder === 1)?.id ?? critLevels[0]?.id ?? 'Supporting';

        const costStr = getValue(['cost', 'price', 'monthly']);
        const monthlyCost = parseFloat(costStr.replace(/[^0-9.]/g, '')) || 0;

        const usersStr = getValue(['users', 'count', 'seats']);
        const userCount = parseInt(usersStr.replace(/[^0-9]/g, '')) || 1;

        const newTool: Tool = {
            id: crypto.randomUUID(),
            name,
            category: category,
            importance,
            pricingModel: 'Fixed', 
            basePrice: monthlyCost,
            userCount: userCount,
            monthlyCost: monthlyCost,
            currency: settings.currency || 'EUR',
            complexity: 2, 
            teamLiteracy: 5, 
            renewalDate: new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0],
            noticeDeadline: new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0],
            satisfactionScore: 70,
            assignedUserIds: [],
            tags: ['imported']
        };

        onAdd(newTool);
        successCount++;
    });

    if (successCount > 0) {
        alert(`Successfully imported ${successCount} tools.`);
    } else {
        alert('No valid tools found in CSV. Please check format: Name, Category, Cost, Users.');
    }
  };

  const getPowerUserName = (id?: string) => {
    if (!id) return 'Unassigned';
    return employees.find(e => e.id === id)?.name || 'Unknown';
  };

  const handleSubmitTask = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!taskTool || !newTaskTitle.trim()) return;
    const newTask: GovernanceTask = {
      id: '',
      title: newTaskTitle.trim(),
      toolId: taskTool.id,
      dueDate: newTaskDueDate || undefined,
      status: 'PENDING',
      priority: 'MEDIUM',
      isCompleted: false
    };
    await onAddGovernanceTask(newTask);
    setTaskTool(null);
    setNewTaskTitle('');
    setNewTaskDueDate('');
  };

  const csvEscape = (val: string | number | undefined): string => {
    const s = String(val ?? '');
    if (s.includes(',') || s.includes('"') || s.includes('\n')) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };

  const CSV_HEADERS = 'Name,Category,Importance,Monthly Cost,User Count,Renewal Date,Notice Deadline,Website,Training URL,Tags,Teams';

  const exportToolsToCsv = () => {
    const header = CSV_HEADERS;
    const rows = filteredTools.map(t => [
      csvEscape(t.name),
      csvEscape(t.category),
      csvEscape((settings.criticalityLevels ?? DEFAULT_CRITICALITY_LEVELS).find(l => l.id === t.importance)?.label ?? t.importance),
      csvEscape(t.monthlyCost),
      csvEscape(t.userCount),
      csvEscape(t.renewalDate),
      csvEscape(t.noticeDeadline),
      csvEscape(t.website),
      csvEscape(t.trainingUrl),
      csvEscape((t.tags || []).join(';')),
      csvEscape((t.teams || []).join(';')),
    ].join(','));
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tools_export_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const downloadTemplate = () => {
    const blob = new Blob([CSV_HEADERS + '\n'], { type: 'text/csv;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'tool_import_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4">
        {/* Search Bar - Expanded */}
        <div className="relative flex-1 w-full md:max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={18} />
            <input 
              type="text" 
              placeholder="Search tools or tags..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm"
            />
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-2 w-full md:w-auto justify-end">
            {/* Filter Toggle */}
            <button 
                onClick={() => setShowFilters(!showFilters)}
                className={`px-3 py-2 rounded-lg border text-sm font-medium flex items-center gap-2 transition-colors ${showFilters ? 'bg-indigo-50 border-indigo-200 text-indigo-700' : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-50'}`}
            >
                <Filter size={16} />
                Filters
            </button>

            {/* Sort Menu */}
            <div className="relative">
                <button 
                    onClick={() => setShowSortMenu(!showSortMenu)}
                    className="px-3 py-2 rounded-lg border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 text-sm font-medium flex items-center gap-2 transition-colors shadow-sm"
                >
                    <ArrowUpDown size={16} />
                    Sort
                </button>
                {showSortMenu && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg border border-slate-100 py-1 z-20">
                        <div className="px-3 py-2 text-xs font-bold text-slate-400 uppercase tracking-wider">Sort By</div>
                        {[
                            { label: 'Name', key: 'name' },
                            { label: 'Monthly Cost', key: 'monthlyCost' },
                            { label: 'User Count', key: 'userCount' },
                            { label: 'Satisfaction', key: 'satisfactionScore' }
                        ].map((option) => (
                            <button
                                key={option.key}
                                onClick={() => handleSort(option.key as SortField)}
                                className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 flex justify-between items-center"
                            >
                                {option.label}
                                {sortConfig.field === option.key && (
                                    <span className="text-indigo-600 text-xs font-bold bg-indigo-50 px-1.5 rounded">
                                        {sortConfig.direction === 'asc' ? 'ASC' : 'DESC'}
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            <button
              onClick={exportToolsToCsv}
              className="bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 px-4 py-2 rounded-lg flex items-center gap-2 transition-colors shadow-sm text-sm font-medium"
            >
              <Download size={18} />
              Export CSV
            </button>
            <button
              onClick={() => setShowTemplateModal(true)}
              className="bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 px-4 py-2 rounded-lg flex items-center gap-2 transition-colors shadow-sm text-sm font-medium"
            >
              <FileDown size={18} />
              Template
            </button>
            <CsvImporter onImport={handleCsvImport} label="Import" />
            
            <button 
            onClick={handleAddNew}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors shadow-sm text-sm font-medium"
            >
            <Plus size={18} />
            Add Tool
            </button>
        </div>
      </div>

      {/* Filter Panel */}
      {showFilters && (
          <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 animate-in slide-in-from-top-2 duration-200">
              <div className="flex flex-wrap gap-4 items-center">
                  <div className="flex-1 min-w-[200px]">
                      <label className="block text-xs font-bold text-slate-500 mb-1">Category</label>
                      <select 
                        value={filterCategory}
                        onChange={(e) => setFilterCategory(e.target.value)}
                        className="w-full px-3 py-2 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white text-sm"
                    >
                        <option value="All">All Categories</option>
                        {(settings.categories || []).map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div className="flex-1 min-w-[200px]">
                      <label className="block text-xs font-bold text-slate-500 mb-1 flex items-center gap-1">Importance <InfoTooltip text="Filter tools by criticality level." /></label>
                      <select
                        data-testid="filter-importance"
                        value={filterImportance}
                        onChange={(e) => setFilterImportance(e.target.value)}
                        className="w-full px-3 py-2 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white text-sm"
                    >
                        <option value="All">All Levels</option>
                        {[...(settings.criticalityLevels ?? DEFAULT_CRITICALITY_LEVELS)].sort((a, b) => a.sortOrder - b.sortOrder).map(l => <option key={l.id} value={l.id}>{l.label}</option>)}
                    </select>
                  </div>
                  <div className="flex items-end">
                      <button 
                        onClick={() => { setFilterCategory('All'); setFilterImportance('All'); }}
                        className="px-3 py-2 text-sm text-slate-500 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors flex items-center gap-1"
                      >
                          <X size={14} /> Clear Filters
                      </button>
                  </div>
              </div>
          </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider font-semibold">
                <th 
                    className="sticky left-0 z-20 bg-slate-50 px-6 py-4 min-w-[240px] cursor-pointer hover:bg-slate-100 transition-colors shadow-[inset_-1px_0_0_#e2e8f0]"
                    onClick={() => handleSort('name')}
                >
                    <div className="flex items-center gap-1">
                        Tool Name {sortConfig.field === 'name' && <ArrowUpDown size={12} />}
                    </div>
                </th>
                <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4">Cost Model</th>
                <th 
                    className="px-6 py-4 text-right cursor-pointer hover:bg-slate-100 transition-colors"
                    onClick={() => handleSort('userCount')}
                >
                     <div className="flex items-center justify-end gap-1">
                        Users {sortConfig.field === 'userCount' && <ArrowUpDown size={12} />}
                    </div>
                </th>
                <th 
                    className="px-6 py-4 text-right cursor-pointer hover:bg-slate-100 transition-colors"
                    onClick={() => handleSort('monthlyCost')}
                >
                    <div className="flex items-center justify-end gap-1">
                        Total/Mo {sortConfig.field === 'monthlyCost' && <ArrowUpDown size={12} />}
                    </div>
                </th>
                <th className="px-6 py-4">Responsible</th>
                <th className="px-6 py-4 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredTools.map(tool => (
                  <tr 
                    key={tool.id} 
                    onClick={() => handleEdit(tool)}
                    className="hover:bg-indigo-50/50 transition-colors cursor-pointer group"
                  >
                    <td className="sticky left-0 z-10 bg-white group-hover:bg-indigo-50/50 px-6 py-4 min-w-[240px] shadow-[inset_-1px_0_0_#e2e8f0]">
                      <div className="flex items-start gap-2">
                          <div>
                            <div className="font-medium text-slate-800 group-hover:text-indigo-700 transition-colors flex items-center gap-1.5">
                                {tool.name}
                            </div>
                            <div className="text-xs text-slate-500">{tool.category}</div>
                          </div>
                      </div>
                      {tool.tags && tool.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1 hidden group-hover:flex">
                              {tool.tags.map(tag => (
                                  <span key={tag} className="px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded text-[10px] font-medium flex items-center gap-0.5">
                                      <Tag size={8} /> {tag}
                                  </span>
                              ))}
                          </div>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      {(() => {
                        const level = getLevelForTool(tool, settings.criticalityLevels ?? DEFAULT_CRITICALITY_LEVELS);
                        const colorCls = COLOR_MAP[level?.color ?? 'gray'] ?? COLOR_MAP.blue;
                        const IconComp = ICON_MAP[level?.icon ?? 'HelpCircle'] ?? HelpCircle;
                        const isTopLevel = level?.sortOrder === 0;
                        return (
                          <span className="inline-flex items-center gap-2">
                            <span className={`w-7 h-7 rounded-full ${colorCls.bg} border ${colorCls.border} flex items-center justify-center ${colorCls.text} shrink-0`} title={level?.label ?? tool.importance}>
                              <IconComp size={14} />
                            </span>
                            <span className={isTopLevel ? `${colorCls.text} font-medium` : 'text-slate-600'}>{level?.label ?? tool.importance}</span>
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <div className="text-slate-700 font-medium">{tool.pricingModel}</div>
                      <div className="text-xs text-slate-500">
                          {tool.pricingModel === 'PerUser' 
                              ? `${getCurrencySymbol(tool.currency, settings)}${tool.basePrice}/user` 
                              : tool.pricingModel === 'Fixed' 
                              ? 'Flat Rate' 
                              : 'Usage Based'}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right text-sm text-slate-600">{tool.userCount}</td>
                    <td className="px-6 py-4 text-right text-sm text-slate-800 font-medium">
                      {getCurrencySymbol(tool.currency, settings)}{tool.monthlyCost.toLocaleString()}
                      {tool.currency && tool.currency !== settings.currency && <span className="text-slate-400 text-xs ml-1">({tool.currency})</span>}
                    </td>
                    <td className="px-6 py-4">
                       <div className="flex items-center gap-2">
                          {tool.powerUserId ? (
                              <>
                                  <div className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-xs font-bold">
                                      {getPowerUserName(tool.powerUserId).charAt(0)}
                                  </div>
                                  <span className="text-sm text-slate-600 truncate max-w-[100px]">{getPowerUserName(tool.powerUserId)}</span>
                              </>
                          ) : (
                              <span className="text-xs text-slate-400 italic">Unassigned</span>
                          )}
                       </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex justify-center gap-2">
                        <button 
                          onClick={(e) => { e.stopPropagation(); setTaskTool(tool); setNewTaskTitle(''); setNewTaskDueDate(''); }}
                          className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-md transition-colors"
                          title="Create a task (Governance)"
                        >
                          <CheckSquare size={18} />
                        </button>
                        <button 
                          onClick={(e) => { e.stopPropagation(); onDelete(tool.id); }}
                          className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                          title="Delete"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
              ))}
              {filteredTools.length === 0 && (
                <tr>
                  <td colSpan={7}>
                    <EmptyState
                      icon={<Search size={40} />}
                      title="No tools match your filters"
                      subtitle="Try adjusting your search, category, or importance filters."
                    />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <ToolModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
        onSave={handleSave} 
        initialData={editingTool} 
        employees={employees}
        curricula={curricula}
        settings={settings}
      />

      {/* Modal : créer une tâche Governance liée à l'outil */}
      {taskTool && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-slate-800 mb-2 flex items-center gap-2">
              <CheckSquare size={20} className="text-indigo-600" />
              New Governance task
            </h3>
            <p className="text-sm text-slate-500 mb-4">Outil : <span className="font-medium text-slate-700">{taskTool.name}</span></p>
            <form onSubmit={handleSubmitTask} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Task title *</label>
                <input
                  type="text"
                  value={newTaskTitle}
                  onChange={e => setNewTaskTitle(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="e.g. Renegotiate contract"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1 flex items-center gap-1"><Calendar size={14} /> Due date</label>
                <input
                  type="date"
                  value={newTaskDueDate}
                  onChange={e => setNewTaskDueDate(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => { setTaskTool(null); setNewTaskTitle(''); setNewTaskDueDate(''); }} className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50">
                  Cancel
                </button>
                <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">
                  Create task
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        current={toolLimit?.current ?? tools.length}
        limit={toolLimit?.limit ?? 25}
        onUpgrade={onUpgrade}
      />

      {/* Template modal: instructions + download */}
      {showTemplateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex justify-between items-center p-4 border-b border-slate-100 bg-slate-50">
              <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                <HelpCircle size={20} className="text-indigo-600" />
                CSV import format
              </h3>
              <button onClick={() => setShowTemplateModal(false)} className="text-slate-400 hover:text-slate-600 p-1">
                <X size={24} />
              </button>
            </div>
            <div className="p-6 overflow-y-auto text-sm text-slate-700 space-y-4">
              <p className="font-medium text-slate-800">Download the template to see the expected columns, then fill in your data.</p>
              <div>
                <h4 className="font-bold text-slate-800 mb-2">Required columns</h4>
                <ul className="list-disc list-inside space-y-1 text-slate-600">
                  <li><strong>Name</strong> — Tool name (required)</li>
                  <li><strong>Category</strong> — Category (e.g. CRM, Communication). Must exist in settings or will be "Other".</li>
                  <li><strong>Importance</strong> — {(settings.criticalityLevels ?? DEFAULT_CRITICALITY_LEVELS).map(l => l.label).join(', ')}</li>
                  <li><strong>Monthly Cost</strong> — Monthly cost (number)</li>
                  <li><strong>User Count</strong> — User count / seats (number)</li>
                </ul>
              </div>
              <div>
                <h4 className="font-bold text-slate-800 mb-2">Optional columns</h4>
                <ul className="list-disc list-inside space-y-1 text-slate-600">
                  <li><strong>Renewal Date</strong>, <strong>Notice Deadline</strong> — Format YYYY-MM-DD</li>
                  <li><strong>Website</strong>, <strong>Training URL</strong> — URLs</li>
                  <li><strong>Tags</strong> — Multiple tags separated by semicolon (;)</li>
                  <li><strong>Teams</strong> — Multiple teams separated by semicolon (;)</li>
                </ul>
              </div>
              <p className="text-slate-500 text-xs">Use a comma to separate columns. If a value contains a comma, wrap it in quotes.</p>
            </div>
            <div className="p-4 border-t border-slate-100 bg-slate-50 flex justify-end gap-2">
              <button
                onClick={() => setShowTemplateModal(false)}
                className="px-4 py-2 border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 font-medium"
              >
                Close
              </button>
              <button
                onClick={() => { downloadTemplate(); setShowTemplateModal(false); }}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium flex items-center gap-2"
              >
                <FileDown size={18} />
                Download template
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};